import { formatScorecardScore } from "../../utils";

import { cn } from "@/lib/utils";

interface LabelMetrics {
  label: string;
  precision: number;
  recall: number;
  f1: number;
  support: number;
}

interface ConfusionMatrix {
  labels: string[];
  counts: number[][];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getClassificationLabels = (aggregateResult: Record<string, unknown> | null) => {
  const labels = aggregateResult?.labels;
  if (!Array.isArray(labels)) {
    return [];
  }
  return labels.filter(isRecord).map(
    (entry): LabelMetrics => ({
      label: String(entry.label ?? ""),
      precision: typeof entry.precision === "number" ? entry.precision : 0,
      recall: typeof entry.recall === "number" ? entry.recall : 0,
      f1: typeof entry.f1 === "number" ? entry.f1 : 0,
      support: typeof entry.support === "number" ? entry.support : 0,
    }),
  );
};

const getConfusionMatrix = (
  aggregateResult: Record<string, unknown> | null,
): ConfusionMatrix | null => {
  const matrix = aggregateResult?.confusion_matrix;
  if (!isRecord(matrix) || !Array.isArray(matrix.labels) || !Array.isArray(matrix.counts)) {
    return null;
  }
  const labels = matrix.labels.map(String);
  const counts = matrix.counts.map(row =>
    Array.isArray(row) ? row.map(count => (typeof count === "number" ? count : 0)) : [],
  );
  return labels.length > 0 ? { labels, counts } : null;
};

const SECTION = "rounded-lg border border-border/70 bg-background";
const SECTION_TITLE =
  "border-b border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground";

const ClassificationReport = ({
  aggregateResult,
}: {
  aggregateResult: Record<string, unknown> | null;
}) => {
  const labels = getClassificationLabels(aggregateResult);
  const matrix = getConfusionMatrix(aggregateResult);
  if (labels.length === 0) {
    return null;
  }
  const isTruncated = aggregateResult?.labels_truncated === true;

  return (
    <>
      <div className={SECTION}>
        <div className={SECTION_TITLE}>Per label</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Label</th>
              <th className="px-2 py-2 text-right font-medium">Precision</th>
              <th className="px-2 py-2 text-right font-medium">Recall</th>
              <th className="px-2 py-2 text-right font-medium">F1</th>
              <th className="px-3 py-2 text-right font-medium">Rows</th>
            </tr>
          </thead>
          <tbody>
            {labels.map(item => (
              <tr key={item.label} className="border-t border-border/60">
                <td
                  className="max-w-[8rem] truncate px-3 py-2 font-medium text-foreground"
                  title={item.label}
                >
                  {item.label}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {formatScorecardScore(item.precision)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {formatScorecardScore(item.recall)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {formatScorecardScore(item.f1)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {item.support}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isTruncated ? (
          <p className="border-t border-border/60 px-3 py-2 text-xs text-muted-foreground">
            Top {labels.length} labels
          </p>
        ) : null}
      </div>

      {matrix ? (
        <div className={SECTION}>
          <div className={SECTION_TITLE}>Confusion matrix</div>
          <div className="overflow-x-auto px-3 py-3">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="pb-1 pr-2 text-left font-normal text-muted-foreground">
                    Expected ↓ Predicted →
                  </th>
                  {matrix.labels.map(label => (
                    <th
                      key={label}
                      className="max-w-[5rem] truncate px-1 pb-1 text-center font-medium text-foreground"
                      title={label}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.labels.map((expectedLabel, rowIndex) => (
                  <tr key={expectedLabel}>
                    <th
                      className="max-w-[7rem] truncate py-0.5 pr-2 text-left font-medium text-foreground"
                      title={expectedLabel}
                    >
                      {expectedLabel}
                    </th>
                    {matrix.labels.map((predictedLabel, columnIndex) => {
                      const count = matrix.counts[rowIndex]?.[columnIndex] ?? 0;
                      const isCorrect = rowIndex === columnIndex;
                      return (
                        <td key={predictedLabel} className="p-0.5">
                          <div
                            className={cn(
                              "min-w-[2.5rem] rounded px-2 py-1.5 text-center tabular-nums",
                              count === 0 && "text-muted-foreground/50",
                              count > 0 &&
                                isCorrect &&
                                "bg-emerald-500/15 font-medium text-foreground",
                              count > 0 &&
                                !isCorrect &&
                                "bg-red-500/10 font-medium text-foreground",
                            )}
                            title={`${count} row${count === 1 ? "" : "s"} expected ${expectedLabel}, predicted ${predictedLabel}`}
                          >
                            {count}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ClassificationReport;
